import React, { lazy, Suspense, useState } from 'react';
import { Box, SxProps } from '@mui/material';
import { isMobileOnly } from 'react-device-detect';
import { env } from 'env';

import HomeBottomButtonOptions from './HomeBottomButtonOptions';
import ExitButton from './ExitButton';
import Actions from './Actions';
import ToolActions from './ToolActions';
import Teleport from './Teleport';
// import ToolBox from './ToolBox';

import { useMytaverseEvent } from '../../providers';

import MytaverseAppBar, {
  AppBarPositionEnum,
} from '@components/MytaverseAppBar';
import VirtualAssistant from './VirtualAssistant/VirtualAssistant';
import ToggleScreenSharingView from './ToggleScreenSharingView';
import Participants from '@core/dashboard/components/HomeBottomBar/Participants';
import RaisedHands from '@core/dashboard/components/HomeBottomBar/RaisedHands';
import UserAvatarButton from '@components/UserAvatarButton';
import AdminTools from '@core/dashboard/components/DashboardTopBar/DashboardTopBarContent/AdminTools';
import VirtualAssistantAppBar from '@core/dashboard/components/HomeBottomBar/VirtualAssistantAppBar';
import HomeBottomBarProvider from './providers/HomeBottomBarProvider';
import SearchUserProvider from '@providers/SearchUserProvider';
import { useChatState } from '@core/chat/context';
import { useMytaverse } from '@providers/MytaverseProvider';
import ParticipantScopes from '@constants/scopes';
import EditorMode from '../EditorMode';
import { useIsAIEnabledForEvent } from '@core/assistant/hooks';
import ReportItDiagnosticsSync from '@components/ReportItDialog/ReportItDiagnosticsSync';
import {
  useClientSpecificGlobalChatVisibility,
  useClientSpecificPrivateChatVisibility,
  useClientSpecificBroadcastChatVisibility,
  useClientSpecificParticipantsVisibility,
  useClientSpecificEmojisVisibility,
  useClientSpecificReactionsVisibility,
  useClientSpecificVirtualAssistant,
} from '@hooks/clientSpecificUtils';
import { useTools } from '@core/dashboard/components/HomeBottomBar/ToolActions/Tools/hooks';
import { useHomeBottomButtonsVisibility } from './HomeBottomButtonOptions/hooks';

const EventSchedulerButton = lazy(() => import('@features/EventScheduler'));
const ChatButton = lazy(() => import('@core/chat/components/ChatButton'));
const AssistantButton = lazy(() => import('@core/assistant'));

const HomeBottomBar = () => {
  const { userPermissions } = useMytaverse();
  const {
    currentRoom,
    showExitButton,
    currentEvent,
    isHideWebUIExceptAssistant,
    isHideAllExceptTopBar,
    canSpeak,
    canShowCamera,
    canShareCamera,
    isMinimizedScreenSharing,
  } = useMytaverseEvent();
  const { REACT_APP_CLASSROOM_EVENT_TAGS, REACT_APP_FEATURE_EVENT_SCHEDULER } =
    env;
  const { isGlobalChatEnabled } = useClientSpecificGlobalChatVisibility();
  const { isPrivateChatEnabled } = useClientSpecificPrivateChatVisibility();
  const { isBroadcastEnabled } = useClientSpecificBroadcastChatVisibility();
  const isAnyChatEnabled =
    isGlobalChatEnabled || isPrivateChatEnabled || isBroadcastEnabled;
  const { isParticipantsEnabled } = useClientSpecificParticipantsVisibility();
  const { isEmojisEnabled } = useClientSpecificEmojisVisibility();
  const { isReactionsEnabled } = useClientSpecificReactionsVisibility();
  const assistantData = useClientSpecificVirtualAssistant();
  const tools = useTools();
  const CLASSROOM_TAGS = (REACT_APP_CLASSROOM_EVENT_TAGS || '')
    .split(',')
    .map((t) => t.toLowerCase().trim());
  const [overwriteAppBarIsOpen, setOverwriteAppBarIsOpen] = useState(true);
  const {
    isUnreadEventChannel,
    isUnreadPrivateChannel,
    isUnreadEventRoomChannel,
  } = useChatState();

  const { hasAnyButton: hasLeftBarButtons } = useHomeBottomButtonsVisibility();

  const isAIEnabledForEvent = useIsAIEnabledForEvent();
  const showInviteUserBtn =
    userPermissions?.includes(ParticipantScopes.HubInviteParticipants) ?? false;

  const isTrainer = userPermissions?.includes(
    ParticipantScopes.HubTrainingToolHubEvent,
  );
  // Check if current event is a classroom event
  const eventTags = currentEvent?.eventTags ?? [];
  const isClassroomEvent = eventTags.some(
    (tag) =>
      typeof tag === 'string' && CLASSROOM_TAGS.includes(tag.toLowerCase()),
  );

  const showTrainerTools = isClassroomEvent && isTrainer;
  const showRaiseHandButton = isClassroomEvent && !isTrainer;
  userPermissions?.includes(ParticipantScopes.HubRiseHand);

  const showDolbyConferenceControls = !!currentRoom;

  // useEffect(() => {
  //   return () => {
  //     if (videoStarted) {
  //       toggleVideo();
  //     }
  //   };
  // }, [videoStarted]);

  if (!showDolbyConferenceControls) {
    return null;
  }

  const isHideUi =
    isHideWebUIExceptAssistant.flag || isHideAllExceptTopBar.flag;

  const isCollapsedBarWithChatNotificationOnMobile =
    (isUnreadEventChannel ||
      isUnreadPrivateChannel ||
      isUnreadEventRoomChannel) &&
    isMobileOnly &&
    !overwriteAppBarIsOpen;

  const renderAssistantButton = (virtualAssistantSx?: SxProps) => {
    if (isAIEnabledForEvent()) {
      return (
        <Box>
          <AssistantButton />
        </Box>
      );
    }

    return <VirtualAssistant sx={virtualAssistantSx} />;
  };

  const renderBottomButtons = () => {
    if (!isMobileOnly && !overwriteAppBarIsOpen) {
      // we need to have the chat icon to be the last element of the bottom bar
      // when not on mobile, so when collapsed it is the chat icon shown.
      return (
        <>
          {REACT_APP_FEATURE_EVENT_SCHEDULER === 'true' ? (
            <Suspense fallback={null}>
              <EventSchedulerButton />
            </Suspense>
          ) : null}
          <ToggleScreenSharingView />
          <Actions />

          {isParticipantsEnabled ? (
            <SearchUserProvider>
              <Participants />
            </SearchUserProvider>
          ) : null}
          <ToolActions />
          {isAnyChatEnabled && !!currentRoom?.isMultiPlayer ? (
            <Suspense fallback={null}>
              <SearchUserProvider>
                <ChatButton />
              </SearchUserProvider>
            </Suspense>
          ) : null}
        </>
      );
    } else {
      if (isCollapsedBarWithChatNotificationOnMobile) {
        // if we have an unread message, are on mobile, and the bar is collapsed
        // we need to move the chat button at the end before the teleport so we can
        // show it
        return (
          <>
            {REACT_APP_FEATURE_EVENT_SCHEDULER === 'true' ? (
              <Suspense fallback={null}>
                <EventSchedulerButton />
              </Suspense>
            ) : null}
            <ToggleScreenSharingView />
            {isParticipantsEnabled ? (
              <SearchUserProvider>
                <Participants />
              </SearchUserProvider>
            ) : null}
            <ToolActions />
            <Actions />

            {isAnyChatEnabled && !!currentRoom?.isMultiPlayer ? (
              <Suspense fallback={null}>
                <SearchUserProvider>
                  <ChatButton />
                </SearchUserProvider>
              </Suspense>
            ) : null}
            <Teleport />
          </>
        );
      } else {
        return (
          <>
            {REACT_APP_FEATURE_EVENT_SCHEDULER === 'true' ? (
              <Suspense fallback={null}>
                <EventSchedulerButton />
              </Suspense>
            ) : null}
            <ToggleScreenSharingView />
            {isAnyChatEnabled && !!currentRoom?.isMultiPlayer ? (
              <Suspense fallback={null}>
                <SearchUserProvider>
                  <ChatButton />
                </SearchUserProvider>
              </Suspense>
            ) : null}
            {isParticipantsEnabled ? (
              <SearchUserProvider>
                <Participants />
              </SearchUserProvider>
            ) : null}
            <ToolActions />
            <Actions />

            {isMobileOnly && <Teleport />}
          </>
        );
      }
    }
  };

  const renderTopButtons = () => (
    <>{showInviteUserBtn ? <AdminTools /> : null}</>
  );

  if (!isMobileOnly && isHideWebUIExceptAssistant.flag) {
    return <VirtualAssistantAppBar />;
  }

  if (!isMobileOnly && isHideAllExceptTopBar.flag) {
    return null;
  }

  const rightBarButtonCount =
    Number(isMinimizedScreenSharing) +
    Number(isAnyChatEnabled && !!currentRoom?.isMultiPlayer) +
    Number(isParticipantsEnabled) +
    Number(isEmojisEnabled || isReactionsEnabled) +
    Number(tools.length > 0) +
    Number(isAIEnabledForEvent()) +
    Number(!!assistantData);

  const hasRightBarButtons = rightBarButtonCount > 0;

  const isHomeBottomButtonsBarCollapsible =
    1 +
    Number(canSpeak) +
    Number(canSpeak && (canShowCamera || canShareCamera)) +
    Number(showRaiseHandButton) +
    Number(showTrainerTools);

  return (
    <HomeBottomBarProvider>
      <ReportItDiagnosticsSync />
      {isHideUi || !hasLeftBarButtons ? null : (
        <MytaverseAppBar
          position={
            isMobileOnly
              ? AppBarPositionEnum.TopLeft
              : AppBarPositionEnum.BottomLeft
          }
          isCollapsible={
            isMobileOnly && isHomeBottomButtonsBarCollapsible > 3 ? true : false
          }
        >
          <HomeBottomButtonOptions />
        </MytaverseAppBar>
      )}
      {isMobileOnly || hasRightBarButtons ? (
        <MytaverseAppBar
          position={
            isMobileOnly
              ? AppBarPositionEnum.TopRight
              : AppBarPositionEnum.BottomRight
          }
          visiblePart={
            !isMobileOnly || isHideAllExceptTopBar.flag
              ? null
              : renderAssistantButton()
          }
          isCollapsible={
            isMobileOnly
              ? !(isHideAllExceptTopBar.flag || isHideWebUIExceptAssistant.flag)
              : rightBarButtonCount > 2
          }
          overwriteAppBarIsOpen={overwriteAppBarIsOpen}
          setOverwriteAppBarIsOpen={setOverwriteAppBarIsOpen}
          collapsedSize={
            isCollapsedBarWithChatNotificationOnMobile ? 112 : undefined
          }
        >
          {/*<ToolBox />*/}

          {isMobileOnly ? renderTopButtons() : null}

          {isHideUi ? null : renderBottomButtons()}

          {isMobileOnly || isHideAllExceptTopBar.flag
            ? null
            : renderAssistantButton()}

          {isMobileOnly ? <UserAvatarButton /> : null}
        </MytaverseAppBar>
      ) : null}
      {showExitButton.flag ? (
        <MytaverseAppBar
          position={AppBarPositionEnum.BottomCenter}
          isCollapsible={false}
        >
          <ExitButton />
        </MytaverseAppBar>
      ) : null}
      <RaisedHands />
      <EditorMode />
    </HomeBottomBarProvider>
  );
};

export default HomeBottomBar;
